/**
 * @fileoverview Facade para la gestión de autenticación API
 * Proporciona una interfaz simplificada para el sistema de autenticación
 * 
 * Este módulo implementa el patrón Facade para ofrecer una interfaz simplificada
 * que encapsula las complejidades del sistema subyacente de autenticación,
 * siguiendo los principios de diseño SOLID:
 * - Single Responsibility: cada clase tiene una única responsabilidad
 * - Open/Closed: extensible sin modificar código existente
 * - Liskov Substitution: las implementaciones de estrategia son intercambiables
 * - Interface Segregation: interfaces específicas para cada necesidad
 * - Dependency Inversion: dependencia de abstracciones, no implementaciones
 * 
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

import ApiTokenService from './ApiTokenService';

/**
 * Facade que proporciona una interfaz simplificada para operaciones de autenticación
 */
class TokenServiceFacade {
  constructor() {
    this.apiTokenService = new ApiTokenService();
  }

  /**
   * Obtiene un token API válido para realizar peticiones autenticadas
   * @param {boolean} forceRefresh Forzar actualización del token
   * @returns {Promise<string>} Token API válido
   */
  async getToken(forceRefresh = false) {
    const tokenData = await this.apiTokenService.getApiToken(forceRefresh);
    return tokenData.token;
  }
  
  /**
   * Método de compatibilidad para código minificado que depende de esta función
   * Asegura que hay un token válido disponible y lo devuelve
   * @param {boolean} forceRefresh Forzar actualización del token
   * @returns {Promise<string>} Token API válido
   */
  async ensureValidToken(forceRefresh = false) {
    return await this.getToken(forceRefresh);
  }

  /**
   * Obtiene la información completa del token API
   * @param {boolean} forceRefresh Forzar actualización del token
   * @returns {Promise<{token: string, format: string, expiresIn: number}>} Información del token
   */
  async getTokenInfo(forceRefresh = false) {
    return await this.apiTokenService.getApiToken(forceRefresh);
  }

  /**
   * Construye cabeceras de autenticación para peticiones API
   * @param {boolean} forceRefresh Forzar actualización del token
   * @returns {Promise<Object>} Cabeceras para petición
   */
  async getAuthHeaders(forceRefresh = false) {
    const tokenData = await this.apiTokenService.getApiToken(forceRefresh);
    return {
      'Authorization': `${tokenData.format} ${tokenData.token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Invalida el token actual
   */
  invalidateToken() {
    this.apiTokenService.invalidateCurrentToken();
  }

  /**
   * Obtiene el estado del token actual
   * @returns {Object|null} Estado del token o null si no hay token
   */
  getTokenStatus() {
    const tokenInfo = {
      source: localStorage.getItem('api_token_source'),
      timestamp: localStorage.getItem('api_session_timestamp'),
      success: localStorage.getItem('api_session_success') === 'true'
    };

    if (!tokenInfo.source) return null;
    return tokenInfo;
  }

  /**
   * Obtiene diagnóstico de errores de autenticación
   * @returns {Object} Información de diagnóstico
   */
  getDiagnostics() {
    return {
      lastError: localStorage.getItem('api_session_error'),
      lastErrorTimestamp: localStorage.getItem('api_session_error_timestamp'),
      convertTokenError: localStorage.getItem('api_convert_token_error'),
      convertTokenErrorTimestamp: localStorage.getItem('api_convert_token_error_timestamp'),
      directTokenError: localStorage.getItem('api_direct_token_error'),
      directTokenErrorTimestamp: localStorage.getItem('api_direct_token_error_timestamp'),
      fallbackReason: localStorage.getItem('api_token_fallback_reason'),
      fallbackTimestamp: localStorage.getItem('api_token_fallback_timestamp'),
      fallbackWarning: localStorage.getItem('api_token_fallback_warning'),
      tiendaId: localStorage.getItem('tiendaId'),
      tiendaIdTimestamp: localStorage.getItem('tiendaId_timestamp')
    };
  }
}

export default TokenServiceFacade;
